import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useReorderSensors } from "@/hooks/useReorderSensors";
import { reorderByDragEnd } from "@/lib/reorder";
import { cn } from "@/lib/utils";
import {
  createColumnCompare,
  initialSortDirectionForKind,
} from "./dataTableSort";
import {
  DataTableShell,
  type DataTableLoadState,
} from "./DataTableShell";
import { DataTableStaticHeader } from "./DataTableStaticHeader";
import { SortableDataTableRow } from "./SortableDataTableRow";
import { SortableHeader } from "./SortableHeader";
import {
  orderColumnsByIds,
  TablePreferencesSlot,
} from "./TablePreferencesSlot";
import {
  FALLBACK_COLUMN_MIN_WIDTH,
  FALLBACK_COLUMN_WIDTH,
  resolveColumnWidthConfigs,
  useColumnResize,
} from "./useColumnResize";
import { TABLE_UTILITY_COLUMN_WIDTH_PX } from "./tableConstants";
import { useSortedPaginatedList } from "./useSortedPaginatedList";
import { useTablePreferences, toPreferenceColumns } from "./useTablePreferences";
import type { DataTableLabels } from "./tableLabels";
import type {
  DataTablePaginationProps,
  DataTableSelectionBulkAction,
  DataTableServerPagination,
  SortDirection,
  SortKind,
  SortValue,
} from "./tableTypes";
import { serverPageWindow } from "@/hooks/useUrlPage";
import { DataTableSelectionToolbar } from "./DataTableSelectionToolbar";

export type { DataTableLoadState };

interface DataTableColumnBase<T> {
  id: string;
  header: string;
  align?: "left" | "center" | "right";
  headerClassName?: string;
  cellClassName?: string;
  cell: (row: T) => ReactNode;
  /**
   * Proportional width weight when resizing is enabled (not a fixed on-screen
   * pixel size). Columns share the full table width by weight / totalWeight.
   */
  defaultWidth?: number;
  minWidth?: number;
  /** Set false for fixed columns (e.g. actions chevron). */
  resizable?: boolean;
  /** Cannot be hidden via table preferences. */
  alwaysVisible?: boolean;
  /** Starts hidden until the user enables it in table preferences. */
  defaultHidden?: boolean;
  /** Stays visible at the right edge while the table scrolls horizontally (row actions). */
  pinRight?: boolean;
}

/** Non-sortable column. */
export type DataTableColumnStatic<T> = DataTableColumnBase<T> & {
  sortKey?: undefined;
  sortKind?: undefined;
  getSortValue?: undefined;
};

/** Sortable column: sortKey and getSortValue are both required. */
export type DataTableColumnSortable<T, SortKey extends string> =
  DataTableColumnBase<T> & {
    sortKey: SortKey;
    sortKind?: SortKind;
    getSortValue: (row: T) => SortValue;
  };

export type DataTableColumn<T, SortKey extends string = string> =
  | DataTableColumnStatic<T>
  | DataTableColumnSortable<T, SortKey>;

function isSortableColumn<T, SortKey extends string>(
  column: DataTableColumn<T, SortKey>,
): column is DataTableColumnSortable<T, SortKey> {
  return column.sortKey !== undefined;
}

import { Checkbox } from "@/components/ui";

export interface DataTableReorderProps<T> {
  disabled?: boolean;
  dragHandleLabel: string;
  onReorder: (reordered: T[]) => void;
}

interface DataTableBaseProps<T, SortKey extends string> {
  rows: T[];
  columns: DataTableColumn<T, SortKey>[];
  getRowKey: (row: T) => string;
  labels: DataTableLabels;
  closeLabel: string;
  loadingLabel: string;
  defaultSortColumn: SortKey;
  defaultSortDirection?: SortDirection;
  /** Persist page size / column visibility / order under this key. */
  preferencesKey: string;
  /** Change this when filters change to jump back to page 1. */
  resetToken?: string | number;
  /** Absent = data ready (success). */
  loadState?: DataTableLoadState;
  emptyMessage: string;
  rowClassName?: string | ((row: T) => string | undefined);
  selectedIds?: ReadonlySet<string>;
  onSelectedIdsChange?: (next: Set<string>) => void;
  /** Hide the default selection bar above the table. */
  hideSelectionToolbar?: boolean;
  /** e.g. select all rows matching parent filters across pages. */
  selectionBulkAction?: DataTableSelectionBulkAction;
  selectionRowAriaLabel?: (row: T) => string;
  /** Row drag-and-drop; disables header sorting while active. */
  reorder?: DataTableReorderProps<T>;
  /** Heading on the left of the table's header row (use `DataTableTitle`). */
  title?: ReactNode;
  /** Controls on the right of the header row, before preferences. */
  actions?: ReactNode;
  toolbar?: ReactNode;
}

export type DataTableProps<T, SortKey extends string> =
  DataTableBaseProps<T, SortKey> &
    (
      | {
          /** Client-side paging of `rows` (default on). */
          paginate?: boolean;
          serverPagination?: undefined;
        }
      | {
          paginate?: undefined;
          /** `rows` is one server page; the header and footer bars drive the page. */
          serverPagination: DataTableServerPagination;
        }
    ) &
    (
      | {
          onRowClick?: undefined;
          openRowLabel?: undefined;
          isRowClickable?: undefined;
        }
      | {
          onRowClick: (row: T) => void;
          openRowLabel: (row: T) => string;
          /** When set with onRowClick, only matching rows are clickable. */
          isRowClickable?: (row: T) => boolean;
        }
    );

const DEFAULT_CELL_CLASS =
  "min-w-0 break-words px-4 py-3 text-sm leading-snug text-surface-600";
const DEFAULT_HEADER_CLASS = "h-10 px-4 py-2 text-left align-middle";

/** Divider + soft shadow on a pinned column, only while rows scroll beneath it. */
const PINNED_EDGE_CLASS =
  "group-data-[overflow-end=true]/table-scroll:shadow-[inset_1px_0_0_var(--color-surface-200),-10px_0_12px_-10px_rgb(0_0_0/0.18)]";
// Opaque equivalents of the translucent header / row-hover fills, so scrolled
// cells do not show through the pinned column.
const PINNED_HEADER_CLASS = cn(
  "sticky right-0 z-[1] bg-[color-mix(in_oklab,var(--color-surface-50)_80%,var(--color-surface-0))] dark:bg-[color-mix(in_oklab,var(--color-surface-100)_40%,var(--color-surface-0))]",
  PINNED_EDGE_CLASS,
);
const PINNED_CELL_CLASS = cn(
  "sticky right-0 z-[1] bg-surface-0 [tr:hover>&]:bg-surface-50 dark:[tr:hover>&]:bg-[color-mix(in_oklab,var(--color-surface-100)_50%,var(--color-surface-0))]",
  PINNED_EDGE_CLASS,
);

export function DataTable<T, SortKey extends string>({
  rows,
  columns,
  getRowKey,
  labels,
  closeLabel,
  loadingLabel,
  defaultSortColumn,
  defaultSortDirection = "asc",
  paginate = true,
  serverPagination,
  preferencesKey,
  resetToken,
  loadState,
  emptyMessage,
  onRowClick,
  openRowLabel,
  isRowClickable,
  rowClassName,
  selectedIds,
  onSelectedIdsChange,
  hideSelectionToolbar = false,
  selectionBulkAction,
  selectionRowAriaLabel,
  reorder,
  title,
  actions,
  toolbar,
}: DataTableProps<T, SortKey>) {
  const { t: tCommon } = useTranslation("common");
  const reorderEnabled = reorder != null;
  const selectionEnabled =
    selectedIds !== undefined && onSelectedIdsChange !== undefined;

  const resolvedLoadState: DataTableLoadState | undefined =
    loadState?.status === "error"
      ? {
          ...loadState,
          message: loadState.message.trim()
            ? loadState.message
            : tCommon("errorOccurred"),
        }
      : loadState;

  const sensors = useReorderSensors();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const preferenceColumns = useMemo(
    () => toPreferenceColumns(columns),
    [columns],
  );

  const {
    preferences,
    pageSize: preferredPageSize,
    orderedVisibleColumnIds,
    applyPreferences,
    resetPreferences,
  } = useTablePreferences({
    tableId: preferencesKey,
    columns: preferenceColumns,
  });

  const visibleColumns = useMemo(
    () => orderColumnsByIds(columns, orderedVisibleColumnIds),
    [columns, orderedVisibleColumnIds],
  );

  const columnWidthConfigs = useMemo(
    () => resolveColumnWidthConfigs(visibleColumns),
    [visibleColumns],
  );

  const widthConfigById = useMemo(() => {
    return new Map(
      columnWidthConfigs.map((config) => [config.id, config] as const),
    );
  }, [columnWidthConfigs]);

  const { widths, startResize, nudgeColumn } = useColumnResize(columnWidthConfigs);

  const totalColumnWidth = useMemo(() => {
    return visibleColumns.reduce(
      (total, column) =>
        total + (widths[column.id] ?? column.defaultWidth ?? FALLBACK_COLUMN_WIDTH),
      0,
    );
  }, [visibleColumns, widths]);

  const sortableColumns = useMemo(
    () =>
      reorderEnabled
        ? []
        : columns.filter(isSortableColumn).map((column) => ({
            sortKey: column.sortKey,
            sortKind: column.sortKind,
            getSortValue: column.getSortValue,
          })),
    [columns, reorderEnabled],
  );

  const compare = useMemo(
    () => createColumnCompare(sortableColumns),
    [sortableColumns],
  );

  const initialDirectionForColumn = useMemo(() => {
    const kindByKey = new Map(
      sortableColumns.map((column) => [column.sortKey, column.sortKind] as const),
    );
    return (column: SortKey) =>
      initialSortDirectionForKind(kindByKey.get(column));
  }, [sortableColumns]);

  const clientPaginate = paginate && !reorderEnabled && !serverPagination;
  const {
    sortColumn,
    sortDirection,
    handleSort,
    resetPage,
    visibleItems,
    showPagination,
    paginationProps,
  } = useSortedPaginatedList<T, SortKey>({
    items: rows,
    compare: reorderEnabled ? () => 0 : compare,
    defaultSortColumn,
    defaultSortDirection,
    initialDirectionForColumn,
    paginate: clientPaginate,
    pageSize: preferredPageSize,
  });

  const previousResetTokenRef = useRef(resetToken);
  useEffect(() => {
    if (resetToken === undefined) {
      return;
    }
    if (previousResetTokenRef.current === resetToken) {
      return;
    }
    previousResetTokenRef.current = resetToken;
    resetPage();
  }, [resetPage, resetToken]);

  // Keep caller order when reordering; sorted/paginated path otherwise.
  const displayItems = reorderEnabled ? rows : visibleItems;
  const effectiveShowPagination = serverPagination
    ? serverPagination.total > 0
    : showPagination;
  const effectivePaginationProps: DataTablePaginationProps = serverPagination
    ? {
        ...serverPageWindow(
          serverPagination.page,
          serverPagination.pageSize,
          serverPagination.total,
        ),
        total: serverPagination.total,
        currentPage: serverPagination.page,
        onPageChange: serverPagination.onPageChange,
        isLoading: serverPagination.isFetching,
      }
    : paginationProps;

  const visibleKeys = useMemo(
    () => displayItems.map(getRowKey),
    [getRowKey, displayItems],
  );

  const displayColumnCount =
    visibleColumns.length + (selectionEnabled ? 1 : 0) + (reorderEnabled ? 1 : 0);

  const preferencesSlot = (
    <TablePreferencesSlot
      labels={labels}
      closeLabel={closeLabel}
      columns={preferenceColumns}
      preferences={preferences}
      onApply={applyPreferences}
      onReset={resetPreferences}
      showPageSize={clientPaginate}
    />
  );

  const handleReorderDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleReorderDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    if (!reorder) {
      return;
    }
    const reordered = reorderByDragEnd(rows, event, (row) => String(getRowKey(row)));
    if (reordered) {
      reorder.onReorder(reordered);
    }
  };

  const activeDragRow =
    activeDragId == null
      ? undefined
      : rows.find((row) => String(getRowKey(row)) === activeDragId);

  const table = (
    <table
      className="w-full table-fixed divide-y divide-surface-200/80"
      style={{ minWidth: `${totalColumnWidth}px` }}
    >
      <colgroup>
        {reorderEnabled ? (
          <col style={{ width: `${TABLE_UTILITY_COLUMN_WIDTH_PX}px` }} />
        ) : null}
        {selectionEnabled ? (
          <col style={{ width: `${TABLE_UTILITY_COLUMN_WIDTH_PX}px` }} />
        ) : null}
        {visibleColumns.map((column) => {
          const weight =
            widths[column.id] ??
            column.defaultWidth ??
            FALLBACK_COLUMN_WIDTH;
          return (
            <col
              key={column.id}
              style={{ width: `${(weight / totalColumnWidth) * 100}%` }}
            />
          );
        })}
      </colgroup>
      <thead className="bg-surface-50/80 dark:bg-surface-100/40">
        <tr>
          {reorderEnabled ? (
            <th className="w-12 px-4 py-2">
              <span className="sr-only">{reorder.dragHandleLabel}</span>
            </th>
          ) : null}
          {selectionEnabled ? (
            <th className="w-12 px-4 py-2">
              <span className="sr-only">{labels.selectionColumn}</span>
            </th>
          ) : null}
          {visibleColumns.map((column) => {
            const widthConfig = widthConfigById.get(column.id);
            const columnResizable = widthConfig?.resizable ?? false;
            const headerClassName = cn(
              DEFAULT_HEADER_CLASS,
              column.align === "right" && "text-right",
              column.align === "center" && "text-center",
              column.pinRight && PINNED_HEADER_CLASS,
              column.headerClassName,
            );
            const columnWeight =
              widths[column.id] ??
              column.defaultWidth ??
              FALLBACK_COLUMN_WIDTH;
            const resizeProps = columnResizable
              ? {
                  resizable: true as const,
                  resizeAriaLabel: labels.resizeColumnFor(column.header),
                  resizeValueNow: columnWeight,
                  resizeValueMin:
                    widthConfig?.minWidth ?? FALLBACK_COLUMN_MIN_WIDTH,
                  resizeValueMax: columnWeight + FALLBACK_COLUMN_WIDTH,
                  onResizeStart: (
                    clientX: number,
                    headerElement: HTMLElement,
                    handleElement: HTMLElement,
                    pointerId: number,
                  ) =>
                    startResize(
                      column.id,
                      clientX,
                      headerElement,
                      handleElement,
                      pointerId,
                    ),
                  onKeyboardResize: (
                    deltaPx: number,
                    headerElement: HTMLElement,
                  ) => nudgeColumn(column.id, deltaPx, headerElement),
                }
              : { resizable: false as const };

            if (!reorderEnabled && isSortableColumn(column)) {
              return (
                <SortableHeader
                  key={column.id}
                  label={column.header}
                  column={column.sortKey}
                  activeColumn={sortColumn}
                  direction={sortDirection}
                  onSort={handleSort}
                  align={column.align}
                  className={headerClassName}
                  {...resizeProps}
                />
              );
            }

            return (
              <DataTableStaticHeader
                key={column.id}
                label={column.header}
                className={headerClassName}
                {...resizeProps}
              />
            );
          })}
        </tr>
      </thead>
      <tbody className="divide-y divide-surface-200/70 bg-surface-0">
        {displayItems.length === 0 ? (
          <tr>
            <td colSpan={displayColumnCount} className="p-0">
              {/* Pinned to the visible width so it stays centered when the columns overflow. */}
              <div className="sticky left-0 w-[var(--table-scroll-width,100%)] px-4 py-12 text-center text-sm text-surface-500">
                {emptyMessage}
              </div>
            </td>
          </tr>
        ) : (
          displayItems.map((row) => {
            const rowKey = getRowKey(row);
            const resolvedRowClassName =
              typeof rowClassName === "function"
                ? rowClassName(row)
                : rowClassName;
            const rowClickable =
              !!onRowClick && (isRowClickable ? isRowClickable(row) : true);
            const activateRow = rowClickable
              ? () => onRowClick?.(row)
              : undefined;
            const rowActivateLabel =
              rowClickable && openRowLabel ? openRowLabel(row) : undefined;

            const cells = (
              <>
                {selectionEnabled ? (
                  <td className="w-12 px-4 py-3 align-middle [&>input]:block">
                    <Checkbox
                      checked={selectedIds.has(rowKey)}
                      aria-label={
                        selectionRowAriaLabel
                          ? selectionRowAriaLabel(row)
                          : labels.selectRow
                      }
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => {
                        const next = new Set(selectedIds);
                        if (next.has(rowKey)) {
                          next.delete(rowKey);
                        } else {
                          next.add(rowKey);
                        }
                        onSelectedIdsChange(next);
                      }}
                    />
                  </td>
                ) : null}
                {visibleColumns.map((column) => {
                  const columnResizable =
                    widthConfigById.get(column.id)?.resizable ?? false;

                  return (
                    <td
                      key={column.id}
                      className={cn(
                        DEFAULT_CELL_CLASS,
                        columnResizable && "overflow-hidden",
                        column.align === "right" && "text-right",
                        column.align === "center" && "text-center",
                        column.pinRight && PINNED_CELL_CLASS,
                        column.cellClassName,
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  );
                })}
              </>
            );

            if (reorderEnabled) {
              return (
                <SortableDataTableRow
                  key={rowKey}
                  id={rowKey}
                  disabled={reorder.disabled}
                  dragHandleLabel={reorder.dragHandleLabel}
                  className={cn(
                    rowClickable ? "cursor-pointer transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50" : undefined,
                    resolvedRowClassName,
                  )}
                  activateLabel={rowActivateLabel}
                  onActivate={activateRow}
                >
                  {cells}
                </SortableDataTableRow>
              );
            }

            return (
              <tr
                key={rowKey}
                className={cn(
                  rowClickable ? "cursor-pointer transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50" : undefined,
                  resolvedRowClassName,
                )}
                tabIndex={rowClickable ? 0 : undefined}
                aria-label={rowActivateLabel}
                onClick={activateRow}
                onKeyDown={
                  rowClickable
                    ? (event) => {
                        if (event.target !== event.currentTarget) {
                          return;
                        }
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          activateRow?.();
                        }
                      }
                    : undefined
                }
              >
                {cells}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  const shelled = (
    <DataTableShell
      labels={labels}
      loadingLabel={loadingLabel}
      loadState={resolvedLoadState}
      showPagination={effectiveShowPagination}
      paginationProps={effectivePaginationProps}
      preferencesSlot={preferencesSlot}
      title={title}
      actions={actions}
      constrainVerticalScroll={reorderEnabled}
      toolbar={
        selectionEnabled && !hideSelectionToolbar ? (
          <DataTableSelectionToolbar
            labels={labels}
            selectedIds={selectedIds}
            onSelectedIdsChange={onSelectedIdsChange}
            visibleRowIds={visibleKeys}
            bulkAction={selectionBulkAction}
            extra={toolbar}
          />
        ) : (
          toolbar
        )
      }
    >
      {table}
    </DataTableShell>
  );

  if (!reorderEnabled) {
    return shelled;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      // Nested table scroller only — never the page. DragOverlay keeps the
      // active row out of flow so transforms do not inflate scrollHeight.
      autoScroll={{
        layoutShiftCompensation: false,
        canScroll: (element) =>
          element !== document.documentElement &&
          element !== document.body &&
          element !== document.scrollingElement,
      }}
      onDragStart={handleReorderDragStart}
      onDragCancel={() => setActiveDragId(null)}
      onDragEnd={handleReorderDragEnd}
    >
      <SortableContext
        items={rows.map(getRowKey)}
        strategy={verticalListSortingStrategy}
      >
        {shelled}
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeDragRow ? (
          <div className="flex max-w-lg items-center gap-3 rounded-lg border border-surface-200 bg-surface-0 px-3 py-2 shadow-popover">
            <GripVertical
              className="h-4 w-4 shrink-0 text-surface-400"
              aria-hidden
            />
            <div className="min-w-0 truncate text-sm text-surface-700">
              {openRowLabel
                ? openRowLabel(activeDragRow)
                : visibleColumns[0]?.cell(activeDragRow)}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
